import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, FileUpload } from '@/shared/ui/atoms';
import { ConfirmDialog } from '@/shared/ui/molecules';

import {
  INVOICE_UPLOAD_ACCEPT,
  useUploadParcelInvoice,
} from '../../../hooks/useUploadParcelInvoice';
import type { PlanWriteError } from '../../../utils/planWriteError';

/** Props de `ParcelInvoiceUploadModal`. */
export interface ParcelInvoiceUploadModalProps {
  /** `null` mantém o diálogo fechado. */
  readonly parcelId: number | null;
  /** Contexto do escopo, exibido sob o título. */
  readonly subtitle?: string | undefined;
  readonly onClose: () => void;
  /** Chamado depois de o backend aceitar o arquivo. */
  readonly onUploaded: () => void;
}

/**
 * Diálogo de envio do comprovante de pagamento de uma parcela.
 *
 * É a ação "Upload de comprovante" da régua da tela de edição, sobre
 * `POST /individualpayment/parcels/{parcelId}/invoice-upload`.
 *
 * **Fica fora do Salvar da tela**, e isso é deliberado: o upload tem efeito
 * imediato e irreversível pela interface, enquanto o Salvar do formulário está
 * bloqueado por lacuna de leitura. Amarrar os dois faria uma ação que funciona
 * depender de um botão que não funciona.
 *
 * O `400` deste endpoint carrega a recusa por **NFe em processamento** na mesma
 * parcela, além do arquivo inválido. Nos dois casos a mensagem vem do backend e
 * é exibida dentro do diálogo, sem fechá-lo — o operador troca o arquivo e tenta
 * de novo sem perder o contexto.
 *
 * Segue o frame `L0ez9` de `design/payment.pen`.
 */
export function ParcelInvoiceUploadModal({
  parcelId,
  subtitle,
  onClose,
  onUploaded,
}: ParcelInvoiceUploadModalProps) {
  const { t } = useTranslation('individualPayment');
  const [file, setFile] = useState<File | null>(null);
  const [failure, setFailure] = useState<PlanWriteError | null>(null);

  const { upload, isPending, reset } = useUploadParcelInvoice(parcelId);

  // O diálogo é mantido montado e fechado por `parcelId: null` — desmontá-lo
  // aberto deixaria o estado do arquivo preso para a próxima abertura.
  useEffect(() => {
    if (parcelId !== null) return;
    setFile(null);
    setFailure(null);
    reset();
  }, [parcelId, reset]);

  const handleConfirm = () => {
    if (file === null) return;
    setFailure(null);

    upload(file, {
      onSuccess: () => {
        setFile(null);
        onUploaded();
      },
      onError: setFailure,
    });
  };

  return (
    <ConfirmDialog
      open={parcelId !== null}
      tone="brand"
      icon={ArrowUpTrayIcon}
      title={t('parcelEdit.upload.title')}
      {...(subtitle === undefined ? {} : { description: subtitle })}
      confirmLabel={t('parcelEdit.upload.confirm')}
      confirmIcon={ArrowUpTrayIcon}
      cancelLabel={t('parcelEdit.upload.cancel')}
      closeAriaLabel={t('parcelEdit.upload.close')}
      isPending={isPending}
      confirmDisabled={file === null}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="flex flex-col gap-xxxs">
        <p className="ts-paragraph-xs text-ctx-content-base">{t('parcelEdit.upload.body')}</p>

        {failure !== null ? (
          <Alert
            type={failure.kind === 'server' ? 'danger' : 'warning'}
            title={
              failure.kind === 'server'
                ? t('parcelEdit.upload.serverError')
                : failure.kind === 'forbidden'
                  ? t('parcelEdit.upload.forbidden')
                  : failure.message === null
                    ? t('parcelEdit.upload.rejectedFallback')
                    : t('parcelEdit.upload.rejected', { message: failure.message })
            }
          />
        ) : null}

        {/* O `FileUpload` fala em lista, mas o endpoint recebe um arquivo por
            request: a lista guarda no máximo um, e escolher outro substitui. */}
        <FileUpload
          files={file === null ? [] : [{ file }]}
          addFiles={files => {
            const [first] = files;
            if (first === undefined) return;
            setFailure(null);
            setFile(first);
          }}
          removeFile={() => setFile(null)}
          accept={INVOICE_UPLOAD_ACCEPT}
          hasError={failure !== null}
          helper={t('parcelEdit.upload.accepted')}
        />
      </div>
    </ConfirmDialog>
  );
}
